import styled from 'styled-components';
import { compose, space, typography } from 'styled-system';
import { SpaceProps, TypographyProps } from '../../../theme/styledProps';
import { EntityTypeDTO } from '../../DocumentVault.meta';

interface StackedEntityIconWrapperProps extends SpaceProps, TypographyProps {
  entityType: EntityTypeDTO;
}

const getOverlayOffset = (
  entityType: EntityTypeDTO,
  size: number,
): { left: string; bottom: string } => {
  switch (entityType) {
    case EntityTypeDTO.Video:
      return { left: '-3px', bottom: `${Math.round(size * 0.3)}px` };
    case EntityTypeDTO.File:
      return { left: '0', bottom: `${Math.round(size * 0.125)}px` };
    case EntityTypeDTO.Folder:
      return { left: '0', bottom: `${Math.round(size * 0.22)}px` };
    default:
      return { left: '0', bottom: `${Math.round(size * 0.3)}px` };
  }
};

const getOverlayIconSize = (entityType: EntityTypeDTO, size: number): number => {
  if (entityType === EntityTypeDTO.Folder) {
    return Math.round(size * 0.52);
  }

  return Math.round(size * 0.58);
};

const getIconSize = (
  theme: { fontSizes: Record<string, number> },
  fontSize?: TypographyProps['fontSize'],
): number => {
  if (fontSize == null) {
    return theme.fontSizes.body;
  }

  return theme.fontSizes[fontSize] ?? theme.fontSizes.body;
};

export const StackedEntityIconWrapper = styled.span<StackedEntityIconWrapperProps>(
  ({ entityType, theme, fontSize }) => {
    const size = getIconSize(theme, fontSize);
    const baseIconSize = Math.round(size * 1.25);
    const overlayIconSize = getOverlayIconSize(entityType, size);
    const overlayOffset = getOverlayOffset(entityType, size);

    return {
      display: 'inline-block',
      position: 'relative',
      width: `${baseIconSize}px`,
      height: `${baseIconSize}px`,
      lineHeight: `${baseIconSize}px`,
      verticalAlign: 'middle',
      flexShrink: 0,

      '& > i:first-child': {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        textAlign: 'center',
        fontSize: `${baseIconSize}px`,
        lineHeight: `${baseIconSize}px`,
      },

      '& > i:last-child': {
        position: 'absolute',
        left: overlayOffset.left,
        right: 0,
        bottom: overlayOffset.bottom,
        textAlign: 'center',
        fontSize: `${overlayIconSize}px`,
        lineHeight: 1,
        zIndex: 1,
      },
    };
  },
  compose(space, typography),
);
